/**
 * XReportPanel — the mid-session X-report (live, non-freezing snapshot of the drawer) plus a
 * Recharts bar visual of the theoretical-drawer composition. Wires the 4 inline states that aren't
 * screen-level: chargement / erreur / vide / (offline + forbidden are owned by the page).
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Badge, Button, Card, EmptyState, ErrorState, LoadingState } from "@/ui";
import { useT } from "@/app";
import { token } from "@/design/theme";
import { useXReport } from "../api";
import { formatCentimes } from "../money";
import { Money } from "./Money";

export interface XReportPanelProps {
  labId: string | null;
  sessionId: string;
  enabled: boolean;
}

export function XReportPanel({ labId, sessionId, enabled }: XReportPanelProps) {
  const { t } = useT();
  const query = useXReport(labId, sessionId, enabled);

  if (query.isLoading) return <LoadingState variant="table" rows={4} />;
  if (query.isError) {
    return (
      <ErrorState
        message={query.error.message}
        code={query.error.code}
        onRetry={() => void query.refetch()}
      />
    );
  }
  const x = query.data;
  if (!x) {
    return <EmptyState title={t("finance.x.title")} description={t("finance.x.subtitle")} />;
  }

  const mouvementsNets =
    x.total_theorique - x.fond_ouverture - x.total_encaisse + x.total_rembourse;

  // Theoretical-drawer composition — signed contributions that sum to total_theorique.
  const chartData = [
    { key: "fond", label: t("finance.x.chart.fond"), value: x.fond_ouverture, positive: true },
    { key: "encaisse", label: t("finance.x.chart.encaisse"), value: x.total_encaisse, positive: true },
    { key: "rembourse", label: t("finance.x.chart.rembourse"), value: -x.total_rembourse, positive: false },
    { key: "mouvements", label: t("finance.x.chart.mouvements"), value: mouvementsNets, positive: mouvementsNets >= 0 },
  ];
  const hasChart = chartData.some((d) => d.value !== 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <Card
        title={t("finance.x.title")}
        actions={
          <div style={{ display: "flex", gap: "var(--nv-s-2)", alignItems: "center" }}>
            <Badge tone={x.frozen ? "warn" : "success"}>
              {x.etat === "fermee" ? t("finance.x.etat.fermee") : t("finance.x.etat.ouverte")}
            </Badge>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              {t("finance.action.refresh")}
            </Button>
          </div>
        }
      >
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: "var(--nv-s-2) var(--nv-s-5)",
            margin: 0,
          }}
        >
          <dt style={{ color: token.slate }}>{t("finance.x.fondOuverture")}</dt>
          <dd style={{ margin: 0, textAlign: "end" }}>
            <Money centimes={x.fond_ouverture} />
          </dd>
          <dt style={{ color: token.slate }}>{t("finance.x.totalEncaisse")}</dt>
          <dd style={{ margin: 0, textAlign: "end" }}>
            <Money centimes={x.total_encaisse} />
          </dd>
          <dt style={{ color: token.slate }}>{t("finance.x.totalRembourse")}</dt>
          <dd style={{ margin: 0, textAlign: "end" }}>
            <Money centimes={x.total_rembourse} />
          </dd>
          <dt style={{ color: token.slate }}>{t("finance.x.mouvementsCount")}</dt>
          <dd className="nv-mono nv-ltr-island" style={{ margin: 0, textAlign: "end" }}>
            {x.mouvements_count}
          </dd>
          <dt style={{ color: token.ink, fontWeight: 600, paddingTop: "var(--nv-s-2)" }}>
            {t("finance.x.totalTheorique")}
          </dt>
          <dd style={{ margin: 0, textAlign: "end", paddingTop: "var(--nv-s-2)" }}>
            <Money centimes={x.total_theorique} strong />
          </dd>
        </dl>
      </Card>

      <Card title={t("finance.x.chart.title")}>
        {hasChart ? (
          <div style={{ width: "100%", height: 240 }} aria-hidden>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={token.lineFaint} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: token.slate }} stroke={token.line} />
                <YAxis
                  tick={{ fontSize: 11, fill: token.slate }}
                  stroke={token.line}
                  tickFormatter={(v: number) => formatCentimes(v, false)}
                  width={72}
                />
                <Tooltip
                  formatter={(v: number) => formatCentimes(v)}
                  contentStyle={{
                    background: token.surface,
                    border: `1px solid ${token.line}`,
                    borderRadius: "8px",
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {chartData.map((d) => (
                    <Cell key={d.key} fill={d.positive ? token.azure : token.danger} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <EmptyState title={t("finance.x.chart.title")} />
        )}
        {/* Accessible, non-visual fallback for the chart (a11y + RTL-safe). */}
        <ul className="nv-sr-only">
          {chartData.map((d) => (
            <li key={d.key}>
              {d.label}: {formatCentimes(d.value)}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
